import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import EditSubAdmin from '../Components/Statefull/EditSubAdmin';
import EditRestaurant from '../Components/Statefull/EditRestaurant/EditRestaurant';
import AddRestaurantNew from '../Components/Statefull/AddRestaurantNew';
import AddSubAdmin from '../Components/Statefull/AddSubAdmin';
import AddStoreItem from '../Components/Statefull/StoreItem/AddStoreItem';
import ManageStoreItem from '../Components/Statefull/StoreItem/StoreItemList';
import { subAdminAtom } from '../Components/Statefull/SubAdmin/SubAdminTable';
import { useAllSubAdmin } from '../Components/Query/AddSubAdminQuery';
import { CircularProgress, Box } from '@mui/material';

export const EditSubAdminWrapper = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const setSubAdminAtom = useSetAtom(subAdminAtom);
  const queryClient = useQueryClient();
  
  // Try to find the sub-admin in cached query data first
  const cachedSubAdmin = useMemo(() => {
    if (!id) return null;
    
    // Get all cached queries for sub-admins
    const queryCache = queryClient.getQueryCache();
    const subAdminQueries = queryCache.findAll({ queryKey: ['getSubAdmin'] });
    
    // Search through cached data
    for (const query of subAdminQueries) {
      const data = query.state.data as any;
      if (data?.data) {
        const found = data.data.find((item: any) => String(item.id) === String(id));
        if (found) return found;
      }
    }
    return null;
  }, [id, queryClient]);
  
  // Fetch all sub-admins (React Query will use cache if available)
  const { data: subAdminData, isLoading } = useAllSubAdmin({
    page: 1,
    limit: 1000, // Get a large number to find the sub-admin
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  useEffect(() => {
    if (!id) {
      navigate('/sub-admin/list');
      return;
    }

    // Use cached data if available, otherwise use fetched data
    const subAdmin = cachedSubAdmin || (subAdminData?.data?.find((item: any) => String(item.id) === String(id)));
    
    if (subAdmin) {
      setSubAdminAtom(subAdmin);
    } else if (!isLoading && subAdminData) {
      // If not found and not loading, redirect back to list
      navigate('/sub-admin/list');
    }
    
    // Cleanup: clear atom when component unmounts
    return () => {
      setSubAdminAtom(null);
    };
  }, [id, cachedSubAdmin, subAdminData, isLoading, setSubAdminAtom, navigate]);

  if (isLoading && !cachedSubAdmin) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <EditSubAdmin onCancel={() => navigate('/sub-admin/list')} />;
};

export const EditRestaurantWrapper = () => {
  const navigate = useNavigate();
  return (
    <EditRestaurant storeType="" onCancel={() => navigate('/manage-store')} isEdit={false} />
  );
};

export const AddRestaurantWrapper = () => {
  const navigate = useNavigate();
  return (
    <AddRestaurantNew storeType="" onCancel={() => navigate('/manage-store')} />
  );
};

export const AddSubAdminWrapper = () => {
  const navigate = useNavigate();
  return <AddSubAdmin onCancel={() => navigate('/sub-admin/list')} />;
};

export const AddStoreItemWrapper = () => {
  const navigate = useNavigate();
  return <AddStoreItem onClose={() => navigate('/manageStoreItem')} />;
};

export const ManageStoreItemWrapper = () => {
  return <ManageStoreItem editCategory={() => {}} modal={false} />;
};
